
import { useEffect, useState } from "react";
import "./PalaceVendorPages.css";

const API =
  process.env.REACT_APP_API_URL || "https://bmtvendorvendorfinal.onrender.com";

const MEAL_PLANS = [
  "ROOM_ONLY",
  "BREAKFAST",
  "BREAKFAST_DINNER",
  "ALL_MEALS",
];

async function api(url, options = {}) {
  const token = localStorage.getItem("vendorToken");

  const response = await fetch(`${API}${url}`, {
    ...options,
    headers: {
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  const result = await response.json();

  if (!response.ok || result.success === false) {
    throw new Error(result.message || "Request failed");
  }

  return result;
}

const getList = (result) =>
  Array.isArray(result) ? result : result?.data || [];

const getId = (value) =>
  typeof value === "string" ? value : value?._id || "";

const initialForm = () => ({
  name: "",
  code: "",
  description: "",
  mealPlan: "ROOM_ONLY",
  basePrice: "",
  extraAdultPrice: "0",
  extraChildPrice: "0",
  extraMattressPrice: "0",
  gstPercentage: "0",
  isRefundable: true,
  cancellationPolicy: "",
  minimumStay: "1",
  maximumStay: "30",
  minimumAdvanceBookingHours: "0",
});

export default function PalaceRatePlans() {
  const [palaces, setPalaces] = useState([]);
  const [categories, setCategories] = useState([]);
  const [plans, setPlans] = useState([]);

  const [palaceId, setPalaceId] = useState("");
  const [roomCategoryId, setRoomCategoryId] = useState("");
  const [editingId, setEditingId] = useState("");

  const [form, setForm] = useState(initialForm());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    loadPalaces();
  }, []);

  useEffect(() => {
    if (palaceId) loadCategories();
  }, [palaceId]);

  useEffect(() => {
    if (roomCategoryId) {
      loadPlans();
    } else {
      setPlans([]);
    }
  }, [roomCategoryId]);

  async function loadPalaces() {
    try {
      const result = await api("/api/palaces");
      const list = getList(result);

      setPalaces(list);

      if (list.length) {
        setPalaceId(getId(list[0]));
      }
    } catch (err) {
      setError(err.message);
    }
  }

  async function loadCategories() {
    try {
      const result = await api(
        `/api/palace-room-categories/palace/${palaceId}`
      );

      const list = getList(result);
      setCategories(list);

      setRoomCategoryId((current) =>
        list.some((c) => getId(c) === current)
          ? current
          : getId(list[0])
      );
    } catch (err) {
      setCategories([]);
      setRoomCategoryId("");
      setError(err.message);
    }
  }

  async function loadPlans() {
    setLoading(true);
    setError("");

    try {
      const result = await api(
        `/api/palace-rate-plans/category/${roomCategoryId}`
      );

      setPlans(getList(result));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const change = (field, value) => {
    setForm((old) => ({ ...old, [field]: value }));
  };

  function resetForm() {
    setEditingId("");
    setForm(initialForm());
  }

  function editPlan(plan) {
    setEditingId(plan._id);

    setForm({
      name: plan.name || "",
      code: plan.code || "",
      description: plan.description || "",
      mealPlan: plan.mealPlan || "ROOM_ONLY",
      basePrice: String(plan.basePrice ?? ""),
      extraAdultPrice: String(plan.extraAdultPrice ?? 0),
      extraChildPrice: String(plan.extraChildPrice ?? 0),
      extraMattressPrice: String(plan.extraMattressPrice ?? 0),
      gstPercentage: String(plan.gstPercentage ?? 0),
      isRefundable: plan.isRefundable !== false,
      cancellationPolicy: plan.cancellationPolicy || "",
      minimumStay: String(plan.minimumStay ?? 1),
      maximumStay: String(plan.maximumStay ?? 30),
      minimumAdvanceBookingHours: String(
        plan.minimumAdvanceBookingHours ?? 0
      ),
    });

    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function savePlan(event) {
    event.preventDefault();
    setError("");
    setMessage("");

    if (!palaceId || !roomCategoryId) {
      setError("Palace aur room category select karo.");
      return;
    }

    if (!form.name.trim() || !form.code.trim() || form.basePrice === "") {
      setError("Plan name, code aur base price required hain.");
      return;
    }

    if (
      Number(form.basePrice) < 0 ||
      Number(form.minimumStay) < 1 ||
      Number(form.maximumStay) < Number(form.minimumStay) ||
      Number(form.gstPercentage) < 0
    ) {
      setError("Pricing, GST aur stay values check karo.");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        ...form,
        palaceId,
        roomCategoryId,
        name: form.name.trim(),
        code: form.code.trim().toUpperCase(),
        basePrice: Number(form.basePrice),
        extraAdultPrice: Number(form.extraAdultPrice || 0),
        extraChildPrice: Number(form.extraChildPrice || 0),
        extraMattressPrice: Number(form.extraMattressPrice || 0),
        gstPercentage: Number(form.gstPercentage || 0),
        minimumStay: Number(form.minimumStay || 1),
        maximumStay: Number(form.maximumStay || 30),
        minimumAdvanceBookingHours: Number(
          form.minimumAdvanceBookingHours || 0
        ),
      };

      const result = await api(
        editingId
          ? `/api/palace-rate-plans/${editingId}`
          : "/api/palace-rate-plans",
        {
          method: editingId ? "PUT" : "POST",
          body: JSON.stringify(payload),
        }
      );

      setMessage(result.message || "Rate plan saved successfully.");
      resetForm();
      await loadPlans();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function togglePlan(plan) {
    setError("");
    setMessage("");

    try {
      const result = await api(
        `/api/palace-rate-plans/${plan._id}/toggle`,
        { method: "PATCH" }
      );

      setPlans((old) =>
        old.map((item) =>
          item._id === plan._id ? result.data : item
        )
      );

      setMessage(result.message || "Rate plan status updated.");
    } catch (err) {
      setError(err.message);
    }
  }

  async function deletePlan(plan) {
    if (!window.confirm(`Delete rate plan "${plan.name}"?`)) {
      return;
    }

    try {
      const result = await api(
        `/api/palace-rate-plans/${plan._id}`,
        { method: "DELETE" }
      );

      setMessage(result.message || "Rate plan deleted.");
      await loadPlans();
    } catch (err) {
      setError(err.message);
    }
  }

  const activeCount = plans.filter((plan) => plan.isActive).length;

  return (
    <div className="pvp-page">
      <div className="pvp-heading">
        <div>
          <span className="pvp-eyebrow">PALACE REVENUE</span>
          <h1>Rate Plans & Pricing</h1>
          <p>Manage room rates, meal inclusions, GST and cancellation rules.</p>
        </div>

        <button
          className="pvp-btn pvp-btn-light"
          onClick={loadPlans}
          disabled={!roomCategoryId}
        >
          Refresh
        </button>
      </div>

      <div className="pvp-stats">
        <div><span>Total plans</span><strong>{plans.length}</strong></div>
        <div><span>Active plans</span><strong>{activeCount}</strong></div>
        <div>
          <span>Room categories</span>
          <strong>{categories.length}</strong>
        </div>
        <div>
          <span>Lowest base price</span>
          <strong>
            {plans.length
              ? `₹${Math.min(
                  ...plans.map((p) => Number(p.basePrice || 0))
                ).toLocaleString("en-IN")}`
              : "—"}
          </strong>
        </div>
      </div>

      {error && <div className="pvp-alert pvp-error">{error}</div>}
      {message && <div className="pvp-alert pvp-success">{message}</div>}

      <section className="pvp-card">
        <div className="pvp-card-title">
          <div>
            <h2>{editingId ? "Edit Rate Plan" : "Create Rate Plan"}</h2>
            <p>Rate plan codes must be unique within a room category.</p>
          </div>

          {editingId && (
            <button
              type="button"
              className="pvp-btn pvp-btn-light"
              onClick={resetForm}
            >
              Cancel Edit
            </button>
          )}
        </div>

        <div className="pvp-form-grid pvp-selectors">
          <label>
            Palace
            <select
              value={palaceId}
              onChange={(e) => {
                setPalaceId(e.target.value);
                resetForm();
              }}
              required
            >
              <option value="">Select palace</option>
              {palaces.map((p) => (
                <option key={p._id} value={p._id}>
                  {p.propertyName || p.name || "Palace"}
                </option>
              ))}
            </select>
          </label>

          <label>
            Room Category
            <select
              value={roomCategoryId}
              onChange={(e) => setRoomCategoryId(e.target.value)}
              required
            >
              <option value="">Select category</option>
              {categories.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.name || c.roomType || "Room category"}
                </option>
              ))}
            </select>
          </label>
        </div>

        <form className="pvp-form-grid" onSubmit={savePlan}>
          <label>
            Plan Name
            <input
              value={form.name}
              onChange={(e) => change("name", e.target.value)}
              placeholder="Deluxe Breakfast Plan"
              required
            />
          </label>

          <label>
            Plan Code
            <input
              value={form.code}
              onChange={(e) =>
                change("code", e.target.value.toUpperCase())
              }
              placeholder="DELUXE-BB"
              required
            />
          </label>

          <label>
            Meal Plan
            <select
              value={form.mealPlan}
              onChange={(e) => change("mealPlan", e.target.value)}
            >
              {MEAL_PLANS.map((meal) => (
                <option key={meal} value={meal}>
                  {meal.replaceAll("_", " ")}
                </option>
              ))}
            </select>
          </label>

          <label>
            Base Price (₹ / night)
            <input
              type="number"
              min="0"
              value={form.basePrice}
              onChange={(e) => change("basePrice", e.target.value)}
              required
            />
          </label>

          <label>
            Extra Adult Price (₹)
            <input
              type="number"
              min="0"
              value={form.extraAdultPrice}
              onChange={(e) => change("extraAdultPrice", e.target.value)}
            />
          </label>

          <label>
            Extra Child Price (₹)
            <input
              type="number"
              min="0"
              value={form.extraChildPrice}
              onChange={(e) => change("extraChildPrice", e.target.value)}
            />
          </label>

          <label>
            Extra Mattress Price (₹)
            <input
              type="number"
              min="0"
              value={form.extraMattressPrice}
              onChange={(e) =>
                change("extraMattressPrice", e.target.value)
              }
            />
          </label>

          <label>
            GST (%)
            <input
              type="number"
              min="0"
              max="100"
              value={form.gstPercentage}
              onChange={(e) => change("gstPercentage", e.target.value)}
            />
          </label>

          <label>
            Minimum Stay (Nights)
            <input
              type="number"
              min="1"
              value={form.minimumStay}
              onChange={(e) => change("minimumStay", e.target.value)}
            />
          </label>

          <label>
            Maximum Stay (Nights)
            <input
              type="number"
              min="1"
              value={form.maximumStay}
              onChange={(e) => change("maximumStay", e.target.value)}
            />
          </label>

          <label>
            Advance Booking (Hours)
            <input
              type="number"
              min="0"
              value={form.minimumAdvanceBookingHours}
              onChange={(e) =>
                change("minimumAdvanceBookingHours", e.target.value)
              }
            />
          </label>

          <label>
            Refund Policy
            <select
              value={String(form.isRefundable)}
              onChange={(e) =>
                change("isRefundable", e.target.value === "true")
              }
            >
              <option value="true">Refundable</option>
              <option value="false">Non-refundable</option>
            </select>
          </label>

          <label className="pvp-span-2">
            Description
            <textarea
              rows="2"
              value={form.description}
              onChange={(e) => change("description", e.target.value)}
              placeholder="Describe what's included in this plan."
            />
          </label>

          <label className="pvp-span-2">
            Cancellation Policy
            <textarea
              rows="3"
              value={form.cancellationPolicy}
              onChange={(e) =>
                change("cancellationPolicy", e.target.value)
              }
              placeholder="Enter cancellation and refund terms."
            />
          </label>

          <div className="pvp-form-actions pvp-span-2">
            <button className="pvp-btn pvp-btn-primary" disabled={saving}>
              {saving
                ? "Saving..."
                : editingId
                ? "Update Rate Plan"
                : "Create Rate Plan"}
            </button>
          </div>
        </form>
      </section>

      <section className="pvp-card">
        <div className="pvp-card-title">
          <div>
            <h2>Existing Rate Plans</h2>
            <p>Edit pricing or activate/deactivate a plan.</p>
          </div>
        </div>

        {loading ? (
          <div className="pvp-empty">Loading rate plans...</div>
        ) : plans.length === 0 ? (
          <div className="pvp-empty">
            <strong>No rate plans yet</strong>
            <span>Create your first plan using the form above.</span>
          </div>
        ) : (
          <div className="pvp-table-wrap">
            <table className="pvp-table">
              <thead>
                <tr>
                  <th>Plan</th>
                  <th>Meal Plan</th>
                  <th>Base / Night</th>
                  <th>Extra Adult</th>
                  <th>GST</th>
                  <th>Refund</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {plans.map((plan) => (
                  <tr key={plan._id}>
                    <td>
                      <strong>{plan.name}</strong>
                      <small>{plan.code}</small>
                    </td>
                    <td>{(plan.mealPlan || "ROOM_ONLY").replaceAll("_", " ")}</td>
                    <td>
                      <strong>
                        ₹{Number(plan.basePrice || 0).toLocaleString("en-IN")}
                      </strong>
                    </td>
                    <td>
                      ₹{Number(plan.extraAdultPrice || 0).toLocaleString("en-IN")}
                    </td>
                    <td>{plan.gstPercentage || 0}%</td>
                    <td>{plan.isRefundable ? "Refundable" : "Non-refundable"}</td>
                    <td>
                      <span
                        className={`pvp-status ${
                          plan.isActive ? "pvp-status-on" : "pvp-status-off"
                        }`}
                      >
                        {plan.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>
                      <div className="pvp-row-actions">
                        <button onClick={() => editPlan(plan)}>Edit</button>
                        <button onClick={() => togglePlan(plan)}>
                          {plan.isActive ? "Deactivate" : "Activate"}
                        </button>
                        <button
                          className="pvp-danger-text"
                          onClick={() => deletePlan(plan)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
